import { Stack } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { FilterChip, RemoveAllFiltersChip } from '@retail/components';
import dayjs from 'dayjs';
import { Brand } from '@retail/auth/types';
import { useCallback, useMemo } from 'react';
import { CampaignMessageKey, useCampaignTFunction } from '@retail/campaign/i18n';
import { CampaignTypeOption } from './CampaignFilters';
import { CampaignDatePreset } from '@retail/campaign/types';

const campignTypeMessageMap: Record<CampaignTypeOption, CampaignMessageKey> = {
  ALL: 'campaign.campaignOptions.all',
  CENTRAL: 'campaign.campaignOptions.central',
  OPTIONAL: 'campaign.campaignOptions.optional',
  SUPPLIER: 'campaign.campaignOptions.supplier',
  LOCAL: 'campaign.campaignOptions.local',
} as const;

interface Props {
  datePreset: CampaignDatePreset | null;
  setDatePreset: (preset: CampaignDatePreset) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  campaignType: CampaignTypeOption;
  setCampaignType: (type: CampaignTypeOption) => void;
  isMandatory: boolean | null;
  setIsMandatory: (value: boolean | null) => void;
  selectedBrandIds: string[];
  setSelectedBrandIds: (ids: string[]) => void;
  availableBrands: Brand[];
  fromDate: Date | null;
  toDate: Date | null;
  setFromDate: (d: Date | null) => void;
  setToDate: (d: Date | null) => void;
  resetFilters: () => void;
  sx?: SystemStyleObject;
}

export function CampaignFilterChips({
  datePreset,
  setDatePreset,
  searchQuery,
  setSearchQuery,
  campaignType,
  setCampaignType,
  isMandatory,
  setIsMandatory,
  selectedBrandIds,
  setSelectedBrandIds,
  availableBrands,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  resetFilters,
  sx,
}: Props) {
  const t = useCampaignTFunction();
  const hasAny = useMemo(
    () =>
      Boolean(
        searchQuery ||
          isMandatory ||
          (campaignType && campaignType !== 'ALL') ||
          selectedBrandIds.length ||
          fromDate ||
          toDate
      ),
    [searchQuery, isMandatory, campaignType, selectedBrandIds, fromDate, toDate]
  );

  const removeBrand = useCallback(
    (id: string) => {
      setSelectedBrandIds(selectedBrandIds.filter((b) => b !== id));
    },
    [selectedBrandIds, setSelectedBrandIds]
  );

  return (
    <Stack flexDirection="row" alignItems="center" gap={1} flexWrap="wrap" sx={sx}>
      {datePreset === 'PAST' && (
        <FilterChip
          name={t('campaign.filters.datePresets.past.label')}
          onDelete={() => setDatePreset('CURRENT_AND_UPCOMING')}
        />
      )}
      {searchQuery && <FilterChip name={searchQuery} onDelete={() => setSearchQuery('')} />}

      {campaignType && campaignType !== 'ALL' && (
        <FilterChip
          name={t(campignTypeMessageMap[campaignType])}
          onDelete={() => setCampaignType('ALL')}
        />
      )}

      {isMandatory && (
        <FilterChip name={t('campaign.columns.mandatory')} onDelete={() => setIsMandatory(false)} />
      )}

      {selectedBrandIds.map((id) => {
        const brand = availableBrands.find((b) => `${b.brandId}` === id);
        return (
          <FilterChip key={id} name={brand ? brand.name : id} onDelete={() => removeBrand(id)} />
        );
      })}

      {fromDate && (
        <FilterChip
          name={`${t('campaign.columns.validFrom')} ${dayjs(fromDate).format('ll')}`}
          onDelete={() => setFromDate(null)}
        />
      )}

      {toDate && (
        <FilterChip
          name={`${t('campaign.columns.validTo')} ${dayjs(toDate).format('ll')}`}
          onDelete={() => setToDate(null)}
        />
      )}

      {hasAny && <RemoveAllFiltersChip resetFilters={resetFilters} />}
    </Stack>
  );
}
